import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BackspaceIcon from '@mui/icons-material/Backspace';
import PaymentIcon from '@mui/icons-material/Payment';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useKioskContext } from '../context/KioskContext';
import SecureLS from 'secure-ls';
import Receipt from '../dialog/Receipt';

const ls = new SecureLS({ encodingType: "aes" });

const CashPayment = ({ total, totalAmount, onBack, onSubmit, cartItems = [], posId, orderType, existingReceiptNumber, orderNumber }) => {
  const { translate, formatPrice, discountDetails, tableNumber, selectedDelivery } = useKioskContext();
  console.log("totalAmount", totalAmount);
  
  const [amountPaid, setAmountPaid] = useState('');
  const [change, setChange] = useState(0);
  const [deliveryRef, setDeliveryRef] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [tenderType, setTenderType] = useState(orderType === 'Delivery' ? 'E-Cash' : 'Cash');
  const [loading, setLoading] = useState(false);
  const [finalAmount2, setFinalAmount2] = useState(0);
  const [ecashRef, setEcashRef] = useState('');
  const [subTenderType, setSubTenderType] = useState('');

  const user = ls.get('user');

  // Calculate the proper total based on discount type, similar to CartReview
  // Calculate subtotal, VAT, etc. just like in CartReview
  
  const subtotal = total;
  const vatAmount = subtotal * (12 / 112);
  const subtotalExcludingVAT = subtotal - vatAmount;
  
  // Apply discount based on discount type
  const parsedTotal =
    discountDetails?.finalTotal ||
    (totalAmount > 0 ? totalAmount / 100 : total);
  
  console.log("Original Total:", total);
  console.log("Discount Type:", discountDetails?.discountType || 'None');
  console.log("Discount Amount:", discountDetails?.discount || 0);
  console.log("Final Total:", parsedTotal);
  console.log("Table Number:", tableNumber);

  useEffect(() => {
    setFinalAmount2(parsedTotal * 100);
  }, [parsedTotal]);

  // Calculate change when amount paid changes
  useEffect(() => {
    const paid = parseFloat((parseFloat(amountPaid) / 100).toFixed(2));
    const total = parseFloat(parsedTotal.toFixed(2));
  
    if (amountPaid && paid >= total) {
      setChange(parseFloat((paid - total).toFixed(2))); 
      setErrorMessage('');
    } else if (amountPaid) {
      setChange(0);
      setErrorMessage(translate('Amount is less than total', {
        en: 'Amount is less than total',
        zh: '金额少于总计',
        th: 'จำนวนเงินน้อยกว่ายอดรวม',
        tl: 'Ang halaga ay mas mababa sa kabuuan'
      }));
    } else {
      setChange(0);
      setErrorMessage('');
    }
  }, [amountPaid, parsedTotal, translate]);

  // Handle number pad button click
  const handleNumberClick = (num) => {
    // Special handling for the 00 button
    if (num === '00') {
      if (amountPaid === '' || amountPaid === '0') {
        // If starting fresh, just set to 0
        setAmountPaid('0');
        return;
      } else {
        // Handle 00 as appending two zeros or shifting decimal
        let value = parseFloat(amountPaid);
        value = value * 100;
        setAmountPaid(value.toFixed(2));
        return;
      }
    }
    
    // Handle decimal point
    if (num === '.') {
      if (!amountPaid.includes('.')) {
        setAmountPaid(amountPaid + '.');
      }
      return;
    }
    
    // Simple digit entry logic - start with cents
    if (amountPaid === '' || amountPaid === '0') {
      // First digit - start with cents (0.0X)
      setAmountPaid('0.0' + num); 
    } else if (amountPaid === '0.0' + amountPaid.charAt(2)) {
      // Have 0.0X, add second decimal digit (0.XY)
      setAmountPaid('0.' + amountPaid.charAt(2) + num);
    } else if (amountPaid === '0.' + amountPaid.charAt(2) + amountPaid.charAt(3)) {
      // Have 0.XY, convert to X.Y0 and add digit
      setAmountPaid(amountPaid.charAt(2) + '.' + amountPaid.charAt(3) + num);
    } else {
      // Normal case - just append digit if we have room
      if (amountPaid.includes('.')) {
        const parts = amountPaid.split('.');
        // If we already have 2 decimal places, shift decimal point right
        if (parts[1] && parts[1].length >= 2) {
          setAmountPaid(parts[0] + parts[1].charAt(0) + '.' + parts[1].charAt(1) + num);
        } else {
          // Otherwise just append the digit
          setAmountPaid(amountPaid + num);
        }
      } else {
        // No decimal point - just append
        setAmountPaid(amountPaid + num);
      }
    }
  };

  // Handle backspace button click
  const handleBackspace = () => {
    setAmountPaid(prev => prev.slice(0, -1));
  };

  // Handle clear button click
  const handleClear = () => {
    setAmountPaid('');
  };

  // Handle quick amount buttons
  const handleQuickAmount = (amount) => {
    // Add to existing amount rather than replace it
    if (amountPaid) {
      const currentAmount = parseFloat(amountPaid);
      const newTotal = currentAmount + amount;
      setAmountPaid(newTotal.toString());
    } else {
      setAmountPaid(amount.toString());
    }
  };

  // Handle API errors
  const handleApiError = (error) => {
    console.error('Error during API call:', error);
    let message = 'An error occurred while processing your payment. Please try again.';
    if (error.response) {
      // The request was made and the server responded with an error status code
      message = error.response.data.message || message;
    }
    setErrorMessage(message);
  };

  // Handle submit button click
  const isEnough = parseFloat((amountPaid / 100).toFixed(2)) >= parseFloat(parsedTotal.toFixed(2));
  const handleSubmit = async () => {
    
    if (amountPaid && isEnough) {
      const paymentData = {
        amountPaid: parseFloat((amountPaid / 100).toFixed(2)),
        change: change,
        finalTotal: total,
        subtotalExcludingVAT: total * (100 / 112),
        vatAmount: total * (12 / 112)
      };
  
      setPaymentDetails(paymentData);
      setLoading(true);
      setErrorMessage('');

      if (tenderType === 'E-Cash' && orderType !== 'Delivery') {
        if (!ecashRef || !subTenderType) {
          setErrorMessage('Reference number and E-Cash type are required.');
          return;
        }
      }
  
      try {
        let receiptNumber;
        let orderNumber;

        if (existingReceiptNumber) {
          console.log("[⚡] Using existing receipt:", existingReceiptNumber);
          receiptNumber = existingReceiptNumber;
          orderNumber = orderNumber || 'UnknownOrder';
        } else {
        const emptyReceiptResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/create-empty-receipt`,
          {
            posId,
            salesType: orderType || 'Dine-In',
            tenderType: tenderType,
            discountType: discountDetails?.discountType || 'None',
            voucherCode: discountDetails?.voucherCode || '',
            discountDesc: discountDetails?.voucherCode || discountDetails?.discountType || '',
            customerId: discountDetails?.customerId || null,
            menuItems: cartItems.map(item => {
              const displayName = item.menuName;
            
              return {
                menuItemId: item._id,
                quantity: item.quantity,
                name: displayName,
                menuName: displayName,
                displayName: displayName,
                chiliLevelData: item.chiliLevelData || null,
                notes: item.notes || '',
                applyDiscount: item.applyDiscount || false,
                discounts   : item.discounts || []
              };
            }),
            tentNumber: tableNumber,
            ...(orderType === 'Delivery' && selectedDelivery
              ? { subTenderType: selectedDelivery }
              : {})
          }
        );
  
          receiptNumber = emptyReceiptResponse.data.receiptNumber;
          orderNumber = emptyReceiptResponse.data.orderNumber;
        } 

        const processOrderPayload = {
          posId,
          receiptNumber,
          tenderedAmount: parseFloat(amountPaid),
          ...(user && {
            cashierName: `${user.firstname} ${user.lastname}`,
            cashierId: user.userId,
          }),
        };

        if (existingReceiptNumber) {
          processOrderPayload.paymentType = tenderType;
        }

        if (orderType === 'Delivery') {
          processOrderPayload.subTenderType = selectedDelivery;
          processOrderPayload.subTenderDetails = deliveryRef;
        }

        if (tenderType === 'E-Cash' && orderType !== 'Delivery') {
          processOrderPayload.subTenderType = subTenderType;
          processOrderPayload.subTenderDetails = ecashRef;
        }
  
        const processOrderResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/process-order`,
          processOrderPayload, 
          { responseType: 'blob' }
          // {
          //   headers: {
          //     'client-type': 'android'
          //   }
          // }
        );
  
        if (processOrderResponse.status === 200 && processOrderResponse.data instanceof Blob) {
          try {
            const blob = processOrderResponse.data;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
        
            const now = new Date();
            const mm = String(now.getMonth() + 1).padStart(2, "0");
            const dd = String(now.getDate()).padStart(2, "0");
            const yyyy = now.getFullYear();
            const filename = `receipt-${receiptNumber || 'NA'}-${mm}-${dd}-${yyyy}.pdf`;
        
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
          } catch (err) {
            console.error("Error downloading receipt blob:", err);
          }
        
          await Swal.fire({
            icon: 'success',
            title: 'Payment Successful',
            confirmButtonText: 'OK',
          });
        
          onSubmit(paymentData);
        }
      } catch (error) {
        console.error('Error processing payment:', error);
        handleApiError(error);
        await Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: 'Please try again.'
        });
      } finally {
        setLoading(false);
      }
    } else {
      setErrorMessage(translate('Amount is less than total', {
        en: 'Amount is less than total',
        zh: '金额少于总计',
        th: 'จำนวนเงินน้อยกว่ายอดรวม',
        tl: 'Ang halaga ay mas mababa sa kabuuan'
      }));
    }
  };

  const printOrderSlipFromResponse = (orderNumber, receiptArray) => {
    const baseHeight = 60;
    const lineHeight = 5;
  
    const totalHeight = baseHeight + receiptArray.length * lineHeight;
  
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [72, totalHeight],
    });
  
    let y = 10;
  
    receiptArray.forEach((entry) => {
      const {
        text = "",
        align = "left",
        fontSize = "normal",
        bold = false,
      } = entry;
  
      let actualFontSize = 9;
      if (fontSize === "double") actualFontSize = 12;
      else if (fontSize === "normal") actualFontSize = 9;
  
      doc.setFontSize(actualFontSize);
      doc.setFont("helvetica", bold ? "bold" : "normal");
  
      let x = 5;
      if (align === "center") {
        x = 36;
      } else if (align === "right") {
        x = 68;
      }
  
      doc.text(text, x, y, {
        align: align === "center" ? "center" : align === "right" ? "right" : "left",
      });
  
      y += lineHeight;
    });
  
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();
    const filename = `Order#${orderNumber}-${mm}-${dd}-${yyyy}.pdf`;
  
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  };
  
  const printTentativeOrderSlip = async () => {
    const lineHeight = 6;
    let y = 10;
  
    const headerLines = 5 * lineHeight;
    const itemLines = cartItems.reduce((lines, item) => {
      const itemName = item.name || item.menuName || "Unnamed";
      const textLines = Math.ceil(itemName.length / 32);
      return lines + textLines * lineHeight + lineHeight;
    }, 0);
    const extraLines = discountDetails?.discount > 0 ? 6 * lineHeight : 4 * lineHeight;
    const totalHeight = y + headerLines + itemLines + extraLines;
  
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [58, totalHeight],
    });
  
    const addText = (text, options = {}) => {
      const {
        align = "left",
        fontSize = 9,
        bold = false,
        wrapWidth = 48,
      } = options;
  
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", bold ? "bold" : "normal");
  
      const lines = doc.splitTextToSize(text, wrapWidth);
      lines.forEach((line) => {
        let x = 5;
        if (align === "center") x = 29;
        if (align === "right") x = 54;
        doc.text(line, x, y, { align });
        y += lineHeight;
      });
    };
  
    addText("Tentative Order", { align: "center", fontSize: 12, bold: true });
    addText("NOT AN OFFICIAL INVOICE", { align: "center", fontSize: 9 });
    addText(`Table #: ${tableNumber || "N/A"}`, { align: "center" });
    y += 4;
  
    // Step 1: Compute total before discount
    let total = 0;
    cartItems.forEach((item) => {
      const itemName = item.name || item.menuName || "Unnamed";
      const qty = item.quantity || 1;
      const price = item.totalPrice && item.quantity ? (item.totalPrice / item.quantity) * 100 : 0;
      const subtotal = price * qty;
      total += subtotal;
  
      addText(`${qty} x ${itemName}`, { wrapWidth: 48 });
      addText(`PHP ${subtotal.toFixed(2)} (${price.toFixed(2)} ea)`, { align: "right" });
    });
  
    y += 2;
    doc.setDrawColor(0);
    doc.line(5, y, 54, y);
    y += 4;
  
    const discountType = discountDetails?.discountType || '';
    const discountLabel = discountType ? `${discountType} | Discount:` : `Discount:`;
    const discount = (discountDetails?.discount || 0) * 100;
    let vatAmount = 0;
    let vatExempt = 0;
    cartItems.forEach(item => {
      const isDiscounted = item.applyDiscount === true;
      const qty = item.quantity || 1;
      const price = item.totalPrice && item.quantity
        ? (item.totalPrice / item.quantity) * 100
        : 0;
      const subtotal = price * qty;
      if (isDiscounted) {
        vatExempt += subtotal * (12 / 112);
      }
      if (discountType === 'Voucher') {
        vatAmount += subtotal * (12 / 112);
      }
    
      if (!isDiscounted) {
        const itemVAT = subtotal * (12 / 112);
        vatAmount += itemVAT;
      }
    });
  
    // Step 2: Fetch service charge (if Dine-In)
    let serviceChargeAmt = 0;
    let serviceChargePercent = 0;
  
    if (orderType === 'Dine-In') {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/service-charge-public?posId=${posId}`);
        const data = res.data;
        if (data.serviceCharge) {
          serviceChargePercent = parseFloat(data.serviceCharge);
          serviceChargeAmt = total * (serviceChargePercent / 100);
        }
      } catch (e) {
        console.warn("Failed to fetch service charge:", e);
      }
    }
  
    // Step 3: Calculate finalAmount
    let finalAmount = total;

    if (discountType === 'Athlete') {
      finalAmount = total - discount;
    } else if (discountType === 'Diplomat') {
      finalAmount = total - discount;
    } else if (discountType === 'Voucher') {
      finalAmount = total - discount;
    } else {
      finalAmount = total - discount - vatExempt;
    }

    finalAmount += serviceChargeAmt;
  
    // Step 4: Print
    addText(`Total: PHP ${total.toFixed(2)}`, {
      align: "right",
      bold: true,
      fontSize: 10,
    });
  
    if (serviceChargeAmt > 0) {
      addText(`Service Charge (${serviceChargePercent}%): PHP ${serviceChargeAmt.toFixed(2)}`, {
        align: "right",
        fontSize: 9,
      });
    }
  
    if (discount > 0 && discountType) {
      addText(`VAT: PHP ${vatAmount.toFixed(2)}`, {
        align: "right",
        fontSize: 9,
      });
      addText(`${discountLabel} -PHP ${discount.toFixed(2)}`, {
        align: "right",
        fontSize: 9,
      });
    }
  
    addText(`Final Amount: PHP ${finalAmount.toFixed(2)}`, {
      align: "right",
      bold: true,
      fontSize: 10,
    });
  
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();
    const filename = `Tentative-Order-${mm}-${dd}-${yyyy}.pdf`;
  
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  };

  // Generate number pad buttons
  const renderNumberPad = () => {
    const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '00'];
    return (
      <Grid container spacing={2}>
        {numbers.map((num) => (
          <Grid item xs={4} key={num}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleNumberClick(num)}
              sx={{
                py: 2,
                fontSize: '1.5rem',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {num}
            </Button>
          </Grid>
        ))}
        <Grid item xs={8}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={handleClear}
            sx={{
              py: 2,
              fontSize: '1.2rem',
            }}
          >
            {translate('Clear', {
              en: 'Clear',
              zh: '清除',
              th: 'ล้าง',
              tl: 'I-clear'
            })}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleBackspace}
            sx={{
              py: 2,
              fontSize: '1.2rem',
            }}
          >
            <BackspaceIcon />
          </Button>
        </Grid>
      </Grid>
    );
  };

  // Generate quick amount buttons
  const renderQuickAmounts = () => {
    // Generate common denominations based on total
    const roundedTotal = Math.ceil(parsedTotal);
    // Using smaller values since formatPrice will multiply by 100
    const quickAmounts = [
      50, // Will display as PHP 50.00
      100,   // Will display as PHP 100.00
      200,   // Will display as PHP 200.00
      500,   // Will display as PHP 500.00
      1000,  // Will display as PHP 1000.00
      //exact amount
      totalAmount > 0
        ? parseFloat(totalAmount.toFixed(2))
        : parseFloat((parsedTotal * 100).toFixed(2))
    ];

    return (
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            {translate('Quick Amounts', {
              en: 'Quick Amounts',
              zh: '快速金额',
              th: 'จำนวนเงินด่วน',
              tl: 'Mabilis na Halaga'
            })}
          </Typography>
        </Grid>
        {quickAmounts.map(amount => (
          <Grid item xs={4} key={amount}>
            <Button
              // if parsedTotal = amount change background color to green
              variant='outlined'
              fullWidth
              onClick={() => handleQuickAmount(amount)}
              sx={{ mb: 1, borderColor: parsedTotal*100 === amount ? 'green' : '', color: parsedTotal*100 === amount ? 'green' : '', fontWeight: parsedTotal*100 === amount ? 'bold' : '' }}
            >
              + PHP {amount.toFixed(2)}
            </Button>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 3,
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={onBack} 
          color="primary"
          sx={{ mr: 2, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.default' } }}
        >
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        
        <Typography variant="h4" component="h1">
          {translate('Cash Payment', {
            en: 'Cash Payment',
            zh: '现金支付',
            th: 'ชำระเงินสด',
            tl: 'Bayad sa Cash'
          })}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="h5" gutterBottom>
              {translate('Enter Amount Received', {
                en: 'Enter Amount Received',
                zh: '输入收到的金额',
                th: 'ป้อนจำนวนเงินที่ได้รับ',
                tl: 'Ilagay ang Halaga na Natanggap'
              })}
            </Typography>

            {/* Amount display */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'right',
                mb: 3,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                borderRadius: 1,
                backgroundColor: errorMessage ? '#fff4f4' : '#f9f9f9',
                borderColor: errorMessage ? 'error.main' : 'inherit',
              }}
            >
              <Typography variant="h2" color={errorMessage ? 'error' : 'inherit'}>
                {amountPaid ? 'PHP ' + amountPaid : '0'}
              </Typography>
            </Paper>

            {errorMessage && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {errorMessage}
              </Typography>
            )}

            {/* Quick amount buttons */}
            {renderQuickAmounts()}

            <Divider sx={{ my: 2 }} />

            {/* Number pad */}
            {renderNumberPad()}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h5" gutterBottom>
              {translate('Payment Summary', {
                en: 'Payment Summary',
                zh: '支付摘要',
                th: 'สรุปการชำระเงิน',
                tl: 'Buod ng Bayad'
              })}
            </Typography>

            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    {translate('Total Amount', {
                      en: 'Total Amount',
                      zh: '总金额',
                      th: 'จำนวนเงินทั้งหมด',
                      tl: 'Kabuuang Halaga'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                {console.log("Using totalAmount:", totalAmount > 0)}
                {console.log("totalAmount:", totalAmount)}
                  <Typography variant="body1" fontWeight="bold">
                    {totalAmount > 0
                      ? formatPrice(parsedTotal)
                      : formatPrice(parsedTotal)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body1">
                    {translate('Amount Paid', {
                      en: 'Amount Paid',
                      zh: '已付金额',
                      th: 'จำนวนเงินที่ชำระ',
                      tl: 'Halagang Binayaran'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" fontWeight="bold">
                    {amountPaid ? 'PHP ' + amountPaid : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {translate('Change', {
                      en: 'Change',
                      zh: '找零',
                      th: 'เงินทอน',
                      tl: 'Sukli'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {change > 0 ? formatPrice(change) : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {orderType === 'Delivery' && (
              <Box sx={{ mb: 2 }}>
               <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {selectedDelivery} Ref. No. <span style={{ color: 'red' }}>*</span>
               </Typography>
                <input
                  type="text"
                  value={deliveryRef}
                  onChange={(e) => setDeliveryRef(e.target.value)}
                  placeholder="Enter delivery reference number"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '6px',
                    border: '1px solid #ccc'
                  }}
                />
              </Box>
            )}

            {tenderType === 'E-Cash' && orderType !== 'Delivery' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  E-Cash Sub Tender Type <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={subTenderType === 'QRPh' ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => setSubTenderType('QRPh')}
                    sx={{ flex: 1 }}
                  >
                    QRPh
                  </Button>
                  <Button
                    variant={subTenderType === 'Card' ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => setSubTenderType('Card')}
                    sx={{ flex: 1 }}
                  >
                    Card
                  </Button>
                </Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Reference Number <span style={{ color: 'red' }}>*</span>
                </Typography>
                <input
                  type="text"
                  value={ecashRef}
                  onChange={(e) => setEcashRef(e.target.value)}
                  placeholder="Enter reference number"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    marginBottom: '12px'
                  }}
                />
              </Box>
            )}

            {orderType !== 'Delivery' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                <Button
                  variant={tenderType === 'Cash' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setTenderType('Cash')}
                  sx={{ flex: 1 }}
                >
                  Cash
                </Button>
                <Button
                  variant={tenderType === 'E-Cash' ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => setTenderType('E-Cash')}
                  sx={{ flex: 1 }}
                >
                  E-Cash
                </Button>
              </Box>
            )}
            {orderType === 'Delivery' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  Payment Type: E-Cash
                </Typography>
              </Box>
            )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <PaymentIcon />}
              onClick={handleSubmit}
              disabled={
                !amountPaid ||
                !isEnough ||
                loading ||
                (tenderType === 'E-Cash' && orderType !== 'Delivery' && (!ecashRef || !subTenderType)) ||
                (orderType === 'Delivery' && !deliveryRef)
              }
              sx={{ flex: 2, py: 2, fontSize: '1.2rem' }}
            >
              {loading
                ? translate('Processing...', {
                    en: 'Processing...',
                    zh: '处理中...',
                    th: 'กำลังประมวลผล...',
                    tl: 'Pinoproseso...'
                  })
                : translate('Complete Payment', {
                    en: 'Complete Payment',
                    zh: '完成支付',
                    th: 'ชำระเงินเสร็จสิ้น',
                    tl: 'Kumpletuhin ang Bayad'
                  })}
            </Button>
             <Button
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={() => printTentativeOrderSlip(orderType)}
              sx={{ flex: 1, py: 1.5, fontSize: '1rem' }}
            >
              Print Temp
            </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      
      
      {/* Receipt Dialog */}
      <Receipt
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        orderDetails={{
          ...paymentDetails,
          items: cartItems,
          customerName: discountDetails?.customerName || 'Walk-in Customer',
          discount: discountDetails?.discount || 0,
          amountPaid: parseFloat(amountPaid || '0'),
          change: change
        }}
      />
    </Box>
  );
};

export default CashPayment;